import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Render } from '@/components/render-if'
import { Button } from '@/components/ui'
import { AssinaturaContratoStep } from '@/features/proposta/brasil/components/assinatura-contrato-step'
import { ImobiliariaCorretorStep } from '@/features/proposta/brasil/components/imobiliaria-corretor-step'
import {
  StepPropostaContent,
  StepPropostaFooter,
  StepPropostaHeaderContent,
  StepPropostaHeaderDescription,
  StepPropostaHeaderTitle,
  StepPropostaRoot,
} from '@/features/proposta/brasil/components/layouts'
import { TermosObservacoesDaPropostaStep } from '@/features/proposta/brasil/components/termos-da-proposta-step'
import { CardsEtapas } from '@/features/proposta/brasil/components/ui/card-etapa'
import { obterTodosCompradoresPFEPJ } from '@/features/proposta/brasil/domain/Comprador/merge-comprador-pj-pf'
import type { TPropostaSchemaInput } from '@/features/proposta/brasil/domain/schemas'
import { useStepValidationStrategy } from '@/features/proposta/brasil/hooks'
import { useAlterarParticipacaoImobiliaria } from '@/features/proposta/brasil/hooks/use-alterar-participacao-imobiliaria'
import { useParametrosProposta } from '@/features/proposta/brasil/hooks/use-parametros-proposta'
import { ParticipacaoCompradorContent } from '@/features/proposta/brasil/pages/criar-proposta/etapa-detalhes-da-proposta/participacao-comprador-content'
import type { TPropostaLocationState } from '@/features/proposta/brasil/types/proposta.type'
import { TIPO_ASSINATURA_OPTIONS } from '@/features/proposta/brasil/utils/constants'
import { useTypedLocation } from '@/hooks/use-typed-location'

export const DetalhesDaPropostaStep = () => {
  const { t } = useTranslation('proposta')
  const { state } = useTypedLocation<TPropostaLocationState>()
  const { watch, setValue, trigger, getFieldState, formState, resetField } = useFormContext<TPropostaSchemaInput>()
  const { data: parametros } = useParametrosProposta()
  const { validateStep } = useStepValidationStrategy(trigger)

  const assinaturaContrato = watch('detalhesDaProposta.assinaturaContrato')
  const currentStep = watch('step')

  const compradores = obterTodosCompradoresPFEPJ(
    watch('cadastroCompradores.compradores'),
    watch('cadastroCompradores.clientes'),
    watch('cadastroCompradores.clientesJuridicos'),
  )

  const handlePrevStep = () => {
    setValue('step', currentStep - 1)
  }

  const handleNextStep = async () => {
    const isValid = await validateStep('DetalhesDaPropostaStep')
    if (!isValid) return
    setValue('step', currentStep + 1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const { isDirty: isParticipacoesDirty } = getFieldState(
    'detalhesDaProposta.imobiliariasCorretores.participacoes',
    formState,
  )

  const handleSelecaoOpcaoNaoIncluirParticipacao = (field: { onChange: (value: string) => void }, value: string) => {
    resetField('detalhesDaProposta.imobiliariasCorretores')
    field.onChange(value)
  }

  const handleRadioValueChange = useAlterarParticipacaoImobiliaria({
    handleSelecaoOpcaoNao: handleSelecaoOpcaoNaoIncluirParticipacao,
    deveMostrarDialog: isParticipacoesDirty,
  })

  return (
    <StepPropostaRoot>
      <StepPropostaHeaderContent>
        <StepPropostaHeaderTitle>{t('detalhes_proposta.titulo')}</StepPropostaHeaderTitle>
        <StepPropostaHeaderDescription>{t('detalhes_proposta.subtitulo')}</StepPropostaHeaderDescription>
      </StepPropostaHeaderContent>
      <StepPropostaContent>
        <CardsEtapas type="multiple">
          <Render.If condition={compradores.length > 1}>
            {() => <ParticipacaoCompradorContent compradores={compradores} />}
          </Render.If>
          <ImobiliariaCorretorStep
            exibeFormaPagamentoComissao={parametros?.exibeFormaPagamentoComissao}
            simulacoesIds={state.simulacoesIds}
            percentualCorretagem={parametros?.percentualCorretagem ?? null}
            valorCorretagem={parametros?.valorCorretagem ?? null}
            handleRadioValueChange={handleRadioValueChange}
          />
          <AssinaturaContratoStep />
          <TermosObservacoesDaPropostaStep
            assinaturaContrato={assinaturaContrato ? TIPO_ASSINATURA_OPTIONS[assinaturaContrato] : ''}
            termosDaProposta={parametros?.termosDaProposta ?? ''}
          />
        </CardsEtapas>
      </StepPropostaContent>
      <StepPropostaFooter>
        <Button className="font-nunito h-12" variant="outline" onClick={handlePrevStep}>
          {t('etapa_anterior')}
        </Button>
        <Button className="font-nunito h-12" variant="default" onClick={handleNextStep}>
          {t('proxima_etapa')}
        </Button>
      </StepPropostaFooter>
    </StepPropostaRoot>
  )
}
